import type { OfficesBlockProps, Office } from '../types';
import { theme, sectionColors } from '../theme';

/** Compose a one-line address from the office's parts (skips blanks). */
function formatAddress(o: Office): string {
  const cityState = [o.city, o.state].filter(Boolean).join(', ');
  return [o.address, [cityState, o.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
}

/**
 * Pure office roster. Like the listings/agents blocks the renderer is "dumb": it
 * receives `offices` from the host (tenant-sites AND the builder preview both call
 * GET /mls/offices, which aggregates EVERY office ID on the tenant's MLS
 * integration) and NEVER fetches. Renders a graceful empty state rather than
 * throwing. Framework-agnostic so it transpiles in both Next.js and Vite.
 */
export function OfficesBlock({ props, offices }: { props: OfficesBlockProps; offices: Office[] }) {
  const { heading, limit, layout = 'grid', showAddress = true, showPhone = true } = props;
  const items = typeof limit === 'number' ? offices.slice(0, limit) : offices;
  const colors = sectionColors(props, { bg: 'transparent', text: theme.text });

  if (items.length === 0) {
    return (
      <section className="px-6 py-12 text-center" style={{ ...colors, fontFamily: theme.bodyFont }}>
        <p className="opacity-60">No offices to show yet.</p>
      </section>
    );
  }

  const grid = layout === 'list' ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';

  return (
    <section className="px-6 py-12" style={{ ...colors, fontFamily: theme.bodyFont }}>
      {heading ? (
        <h2 className="mx-auto mb-8 max-w-6xl text-2xl font-semibold" style={{ fontFamily: theme.headingFont }}>
          {heading}
        </h2>
      ) : null}
      <div className={`mx-auto grid max-w-6xl gap-6 ${grid}`}>
        {items.map((o) => {
          const address = formatAddress(o);
          return (
            <div
              key={o.key || o.mlsId || o.name || ''}
              className="rounded-lg border border-black/10 p-5"
              style={{ backgroundColor: theme.surface }}
            >
              <div className="text-lg font-semibold" style={{ fontFamily: theme.headingFont }}>
                {o.name || 'Office'}
              </div>
              {showAddress && address ? (
                <div className="mt-1 text-sm opacity-70">{address}</div>
              ) : null}
              {showPhone && o.phone ? (
                <div className="mt-2 text-sm">
                  <a
                    href={`tel:${o.phone.replace(/[^\d+]/g, '')}`}
                    className="font-medium underline-offset-2 hover:underline"
                    style={{ color: theme.primary }}
                  >
                    {o.phone}
                  </a>
                </div>
              ) : null}
              {o.mlsId ? (
                <div className="mt-3 text-sm">
                  <a
                    href={`/listings?office=${encodeURIComponent(o.mlsId)}`}
                    className="font-medium underline-offset-2 hover:underline"
                    style={{ color: theme.primary }}
                  >
                    View this office&rsquo;s listings →
                  </a>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}
